import { BookOpen, Download, Users } from 'lucide-react';
import { Link } from 'react-router-dom';
import type { Ebook } from '@/types/ebook';
import { EbookMiniCover } from './EbookMiniCover';

interface Props {
  ebook: Ebook;
  chapterCount?: number;
}

export function EbookCard({ ebook, chapterCount = 0 }: Props) {
  const hrs = ebook.page_count ? Math.max(1, Math.ceil(ebook.page_count / 30)) : null;
  const canRead = chapterCount > 0;
  const canDownload = !!ebook.pdf_storage_path;

  return (
    <Link
      to={`/ebook/${ebook.slug}`}
      className="block transition-all hover:-translate-y-0.5"
      style={{
        background: 'linear-gradient(145deg, #012840 0%, #0A1A26 100%)',
        border: '1px solid rgba(5, 242, 242, 0.15)',
        borderRadius: 12,
        padding: 16,
        boxShadow: '0 0 12px rgba(5,242,242,0.05), inset 0 1px 0 rgba(255,255,255,0.03)',
      }}
    >
      <div className="flex gap-3">
        <EbookMiniCover preset={ebook.cover_image_url} title={ebook.title_th} />
        <div className="flex-1 min-w-0">
          <div className="text-[13px] font-medium leading-snug" style={{ color: '#D0E4EE' }}>
            {ebook.title_th}
          </div>
          <div className="text-[11px] mt-1.5" style={{ color: '#3D6D82' }}>
            {hrs ? `~${hrs} ชม. · ` : ''}
            {ebook.page_count ?? 0} หน้า
          </div>
          {(canRead || canDownload) && (
            <div className="flex flex-wrap gap-1.5 mt-2">
              {canRead && (
                <span
                  className="inline-flex items-center"
                  style={{
                    gap: 6,
                    padding: '2px 8px',
                    borderRadius: 6,
                    fontSize: 10,
                    background: 'rgba(5,242,242,0.08)',
                    border: '1px solid rgba(5,242,242,0.25)',
                    color: '#05F2F2',
                  }}
                >
                  <BookOpen size={11} />
                  อ่านบนเว็บ
                </span>
              )}
              {canDownload && (
                <span
                  className="inline-flex items-center"
                  style={{
                    gap: 6,
                    padding: '2px 8px',
                    borderRadius: 6,
                    fontSize: 10,
                    background: 'rgba(101,147,166,0.1)',
                    border: '1px solid rgba(101,147,166,0.3)',
                    color: '#6593A6',
                  }}
                >
                  <Download size={11} />
                  ดาวน์โหลด PDF
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      <div
        className="flex items-center justify-between mt-3 pt-3"
        style={{ borderTop: '1px solid rgba(101,147,166,0.06)' }}
      >
        {ebook.access_type === 'free' ? (
          <span
            className="text-[11px]"
            style={{
              padding: '3px 10px',
              borderRadius: 20,
              background: 'rgba(74,222,128,0.1)',
              border: '1px solid rgba(74,222,128,0.3)',
              color: '#4ADE80',
            }}
          >
            รับฟรี
          </span>
        ) : ebook.access_type === 'subscriber' ? (
          <span
            className="text-[11px]"
            style={{
              padding: '3px 10px',
              borderRadius: 20,
              background: 'rgba(5,242,242,0.08)',
              border: '1px solid rgba(5,242,242,0.25)',
              color: '#05F2F2',
            }}
          >
            รวมในแพลน Power+
          </span>
        ) : (
          <span className="text-[14px] font-medium" style={{ color: '#F27405' }}>
            ฿{ebook.price_thb ?? 0}
          </span>
        )}

        {ebook.download_count >= 10 && (
          <div className="flex items-center gap-1" style={{ color: '#3D6D82' }}>
            <Users size={12} />
            <span className="text-[10px]">{ebook.download_count} คนอ่านแล้ว</span>
          </div>
        )}
      </div>
    </Link>
  );
}
